import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, glyphs, icons } from "../../Styles";

import { ArchivedReport, TestData as ArchivedReportData } from "../../Assets/TestData/reports";
import { ConditionData } from "../../Assets/TestData/conditions";
import { AnimalGroupData } from "../../Assets/TestData/animalGroups";
import { LocationData } from "../../Assets/TestData/locations";

interface ArchivedReportCard_Props {
  reportId: number
  onReportSelect?: (id: number) => void // TODO: Go to "create report" 
}

export default function ArchivedReportCard({reportId, onReportSelect}: ArchivedReportCard_Props) {
  const {t} = useTranslation()
  const [isSectionHovered, setIsSectionHovered] = useState(false);
  const [report, setReport] = useState<ArchivedReport | undefined>(undefined);

  // Find the report based on reportId
  useEffect(() => {
    const ArchivedReport = ArchivedReportData.find((r) => r.reportNo === reportId);
    setReport(ArchivedReport);
  }, [reportId]);
  
  const reportTitle = reportId 
    ? report?.name
    : t("reports.noReportFound")

  const reportSubTitle = reportId
    ? t("reports.archivedReport")+" "+report?.reportNo
    : t("reports.noReportFound")

  const dataLevel = reportId
    ? report?.dataLevel
    : t("reports.noDataParameterFound")

  const startDate = reportId
    ? report?.dateStart
    : t("reports.noStartDateFound")

  const endDate = reportId
    ? report?.dateEnd
    : t("reports.noEndDateFound")

  const dateSpan = endDate
    ? startDate+" - "+endDate
    : startDate

  const getLocationName = (locationId?: number): string => {
    const location = LocationData.find(l => l.locationId === locationId)
    return location ? location.locationName : t("unknown")+" "+t("animalPages.animal.location"); 
  }

  const getConditionName = (conditionId?: number): string => {
    const condition = ConditionData.find(c => c.conditionId === conditionId);
    return condition ? condition.conditionName : t("unknown")+" "+t("animalPages.animal.condition");
  };

  const getGroupNumber = (groupId?: number): string => {
    const group = AnimalGroupData.find(a => a.groupId === groupId);
    return group ? group.groupName : t("unknown")+" "+t("animalPages.animal.group");
  }

  const getTemplate = (label: string, value: string) => (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", width: "100%" }}>
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "right" }}>{label}:</p>
      <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "left" }}>{value}</p>
    </div>
  );

  const renderFilterParameters = () => {
    if (!report || !report.filterParameter?.length) {
      return (
        <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>
          {t("reports.noFilterParameterFound")}
        </p>
      );
    }
  
    return report.filterParameter.map((param, index) => {
      return (
        <div key={index}>
          {/* Hovedfilter (main) */}
          {param.main && (
            getTemplate(
              t(`${param.main.key}`),
              param.main.key === "groupId"
                ? getGroupNumber(Number(param.main.value))
                : param.main.key === "conditionId"
                ? getConditionName(Number(param.main.value))
                : param.main.key === "locationId"
                ? getLocationName(Number(param.main.value))
                : param.main.value
            )
          )}


          {/* Underfiltre (sub) */}
          {param.sub &&
            Object.entries(param.sub).map(([key, value]) => (
              <div key={key}>
                {getTemplate(
                  t(`${key}`),
                  Array.isArray(value)
                    ? value.map(v => 
                        key === "groupId"
                          ? getGroupNumber(Number(v))
                          : key === "conditionId"
                          ? getConditionName(Number(v))
                          : key === "locationId"
                          ? getLocationName(Number(v))
                          : v
                      ).join(", ")
                    : key === "groupId"
                    ? getGroupNumber(Number(value))
                    : key === "conditionId"
                    ? getConditionName(Number(value))
                    : key === "locationId"
                    ? getLocationName(Number(value))
                    : value
                )}
              </div>
            ))}
        </div>
      );
    });
  };
  

  // Data Params
  const renderDataParameters = () => {
    if (!report || !report.dataParameter.length) return t("reports.noDataParameterFound");

    return (
      <div style={{ }}>
        {report.dataParameter.map((param, index) => (
          <p key={index} style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center" }}>
            {param}
          </p>
        ))}
      </div>
    );
  };

  return (
    <div
      onClick={onReportSelect ? () => onReportSelect(report?.reportNo ?? 0) : undefined } 
      onMouseEnter={() => setIsSectionHovered(true)}
      onMouseLeave={() => setIsSectionHovered(false)} 
      style={{ 
        cursor: "pointer",
        borderRadius: "10px",
        height: "fit-content",
        breakInside: "avoid",
        marginBottom: "20px",
        border: "5px solid"+colors.greyPrimary, 
        minWidth: "320px",
        width: "100%",
        boxSizing: "border-box",
        filter: isSectionHovered ? 'opacity(0.5)' : 'opacity(1)',
        transition: '0.3s'
    }}>

      <div style={{backgroundColor: colors.greyPrimary, minHeight: "50px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", position: "relative", padding: "10px 20px", textAlign: "center" }}>
        <p style={{...fonts.header, ...fonts.fontSize2, color: colors.greyLightTertiary}}>{reportTitle}</p>
        <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary}}>{reportSubTitle}</p>
      </div>

      {/* Date Span */}
      <div style={{width: "100%",}}>
        <div style={{backgroundColor: colors.greyLightSecondary, height: "30px", display: "flex", alignItems: "center", justifyContent: "center"}}>
          <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyPrimary }}>{dateSpan}</p>
        </div>
      </div>

      <div style={{backgroundColor: colors.greyLightTertiary, borderRadius: "0px 0px 5px 5px", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", padding: "20px 30px", gap: "20px"}}>

        {/* Data Level */}
        <div style={{width: "100%"}}>

          <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
            <glyphs.DataLevel fillColor={colors.greyDarkPrimary}/>
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{t("reports.dataLevel")}</p>
          </div>

          <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
            <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center"}}>{dataLevel}</p>
          </div>

        </div>

        {/* Filter Parameter */}
        <div style={{width: "100%"}}>
        
          <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
            <glyphs.FilterParameter fillColor={colors.greyDarkPrimary} />
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{t("reports.filterParameter")}</p>
          </div>
        
          <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
            {renderFilterParameters()}
          </div>
        </div>
        
        {/* Data Parameter */}
        <div style={{width: "100%"}}>
      
          <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center", justifyContent: "center", marginBottom: "5px"}}>
            <glyphs.DataParameter fillColor={colors.greyDarkPrimary}/>
            <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{t("reports.dataParameter")}</p>
          </div>
      
          <div style={{backgroundColor: colors.greyLightSecondary, padding: "10px", borderRadius: "10px"}}>
            <span style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, textAlign: "center"}}>
              {renderDataParameters()}
            </span>
          </div>

        </div>
        
      </div>

    </div>
  )
}